import { useEffect, useCallback, useRef } from "react";

export type ToastType = "success" | "error" | "info";

export interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
}

interface ToastProps {
  toast: ToastItem;
  onRemove: (id: string) => void;
}

export function Toast({ toast, onRemove }: ToastProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (toast.duration > 0) {
      timerRef.current = setTimeout(() => {
        onRemove(toast.id);
      }, toast.duration);
    }
    return clearTimer;
  }, [toast.id, toast.duration, onRemove, clearTimer]);

  const colors: Record<ToastType, string> = {
    success: "bg-brand-green-600",
    error: "bg-red-600",
    info: "bg-gray-800",
  };

  const iconMap: Record<ToastType, string> = {
    success: "ri-checkbox-circle-line",
    error: "ri-error-warning-line",
    info: "ri-information-line",
  };

  return (
    <div className="flex items-start gap-3 px-4 py-3 rounded-xl text-white shadow-lg max-w-sm w-full animate-in slide-in-from-right duration-300">
      <div className={`${colors[toast.type]} flex items-start gap-3 rounded-xl px-4 py-3 w-full`}>
        <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
          <i className={`${iconMap[toast.type]} text-lg`} />
        </div>
        <p className="text-sm font-medium leading-snug flex-1">{toast.message}</p>
        <button
          onClick={() => {
            clearTimer();
            onRemove(toast.id);
          }}
          className="shrink-0 mt-0.5 opacity-80 hover:opacity-100 transition-opacity"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-close-line" />
          </div>
        </button>
      </div>
    </div>
  );
}